import { useState } from "react";
import { api } from "../api/client.js";
import { getAdminToken } from "./auth.js";
import CandleProductForm from "./CandleProductForm.js";

const getErrorMessage = (error) => {
  if (Array.isArray(error.details) && error.details.length > 0) {
    return error.details
      .map((detail) => `${detail.field}: ${detail.message}`)
      .join(" - ");
  }

  return error.message || "The candle set could not be saved";
};

function CandleManager({
  candles,
  loadError,
  products,
  onChanged,
  onUnauthorized,
}) {
  const [editingCandle, setEditingCandle] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const startCreate = () => {
    setEditingCandle(null);
    setError("");
    setNotice("");
    setIsFormOpen(true);
  };

  const startEdit = (candle) => {
    setEditingCandle(candle);
    setError("");
    setNotice("");
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setEditingCandle(null);
    setError("");
    setIsFormOpen(false);
  };

  const handleChanged = async (message) => {
    setNotice(message);
    setEditingCandle(null);
    setIsFormOpen(false);
    await onChanged();
  };

  const handleRequestError = (requestError) => {
    if (requestError.status === 401 || requestError.status === 403) {
      onUnauthorized();
      return;
    }
    setError(getErrorMessage(requestError));
  };

  const handleDelete = async (candle) => {
    const confirmed = window.confirm(
      `Delete candle set "${candle.fragranceNote}"? This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingId(candle.id);
    setError("");
    setNotice("");

    try {
      await api.admin.candleProducts.remove(candle.id, getAdminToken());
      setNotice(`${candle.fragranceNote} was deleted.`);
      if (editingCandle?.id === candle.id) closeForm();
      await onChanged();
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className="admin-panel admin-products-panel">
      <header className="admin-panel-heading">
        <div>
          <p className="admin-eyebrow">Candle set management</p>
          <h2>Candle sets</h2>
        </div>
        <button type="button" onClick={startCreate}>
          Add candle set
        </button>
      </header>

      {notice && (
        <p className="admin-form-notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="admin-form-error" role="alert">
          {error}
        </p>
      )}
      {loadError && (
        <p className="admin-form-error" role="alert">
          {loadError}
        </p>
      )}

      {isFormOpen && (
        <CandleProductForm
          candleProduct={editingCandle}
          products={products}
          onCancel={closeForm}
          onChanged={handleChanged}
          onUnauthorized={onUnauthorized}
        />
      )}

      <div className="admin-product-grid">
        {candles.map((candle) => (
          <article
            className="admin-product-card admin-candle-card"
            key={candle.id}
          >
            <div className="admin-product-card-body">
              <header>
                <div>
                  <h3>{candle.fragranceNote}</h3>
                  <small>{candle.id}</small>
                  {candle.inStock === false && <small>Out of stock</small>}
                </div>
                <strong>
                  {new Intl.NumberFormat("en", {
                    style: "currency",
                    currency: "USD",
                  }).format(candle.price)}
                </strong>
              </header>
              <dl>
                <div>
                  <dt>Set note</dt>
                  <dd>{candle.fragranceNote}</dd>
                </div>
                <div>
                  <dt>Count</dt>
                  <dd>{candle.candleCount || "Pending setup"}</dd>
                </div>
                <div>
                  <dt>Mood</dt>
                  <dd>{candle.mood}</dd>
                </div>
                <div>
                  <dt>Color</dt>
                  <dd>
                    <i style={{ backgroundColor: candle.color.value }} />
                    {candle.color.name}
                  </dd>
                </div>
                <div>
                  <dt>Products</dt>
                  <dd>
                    {candle.products?.length
                      ? candle.products.map((product) => product.name).join(", ")
                      : "None linked"}
                  </dd>
                </div>
              </dl>
              <div className="admin-product-actions">
                <button type="button" onClick={() => startEdit(candle)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="admin-delete"
                  disabled={deletingId === candle.id}
                  onClick={() => handleDelete(candle)}
                >
                  {deletingId === candle.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </article>
        ))}

        {candles.length === 0 && (
          <p className="admin-empty-copy">
            No candle sets yet. Add the first one.
          </p>
        )}
      </div>
    </section>
  );
}

export default CandleManager;
